// A seat's metric set and weights (`GET` / `PUT /seats/{id}/metrics`, `kpi`; coverage row 26).
// The editor starts from the set itself, every line (Off ones too) with its `penalty_only` flag,
// and saves the whole set under `If-Match` (the set's version): a set someone changed since is
// refused (409) and the editor offers to reload it, so a save never drops what it did not show
// (KPI-FIX #1). Used in the seat drawer and Setup › Metrics › Seat weights.

import { ApiError } from "@companyos/sdk";
import {
  Button,
  Checkbox,
  Field,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownIcon, ArrowUpIcon, XIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { SeatMetric, SeatMetricSet } from "../../api/types";
import type { CompanyAccess } from "../company";
import { keys, metricsQuery, seatMetricSetQuery } from "../queries";
import { Loading } from "../screen-parts";
import { useEveryPage } from "../use-every-page";
import { TRACKING_OPTIONS } from "./labels";
import { ErrorNotice, FormError, ReadOnlyNotice } from "./parts";

type Row = {
  metric_def_id: string;
  name: string;
  kpi_weight: string;
  goal_weight: string;
  tracking_mode: SeatMetric["tracking_mode"];
  penalty_only: boolean;
};

const rowsOf = (set: SeatMetricSet): Row[] =>
  [...set.data]
    .sort((a, b) => a.position - b.position)
    .map((m) => ({
      metric_def_id: m.metric_def_id,
      name: m.metric_name,
      kpi_weight: String(Number(m.kpi_weight)),
      goal_weight: String(Number(m.goal_weight)),
      tracking_mode: m.tracking_mode,
      penalty_only: m.penalty_only,
    }));

const WEIGHT = /^\d{1,3}(\.\d{1,3})?$/;

export function SeatMetricsEditor({
  api,
  company,
  seatId,
  readOnly,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  readOnly: boolean;
}) {
  const set = useQuery(seatMetricSetQuery(api, company.id, seatId));
  // Kept here: a save stores the saved set, which remounts the editor with it. Keyed by the
  // version, not the fetch time: a background refetch of an unchanged set keeps unsaved edits.
  const [savedAt, setSavedAt] = useState<number | null>(null);
  if (set.isPending) return <Loading label="Loading the seat's metrics" rows={3} />;
  if (set.isError) return <ErrorNotice what="The seat's metrics" error={set.error} />;
  return (
    <SetEditor
      key={`${seatId}@${set.data.version}`}
      api={api}
      company={company}
      seatId={seatId}
      version={set.data.version}
      initial={rowsOf(set.data)}
      readOnly={readOnly}
      savedAt={savedAt}
      setSavedAt={setSavedAt}
    />
  );
}

function SetEditor({
  api,
  company,
  seatId,
  version,
  initial,
  readOnly,
  savedAt,
  setSavedAt,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  version: number;
  initial: Row[];
  readOnly: boolean;
  savedAt: number | null;
  setSavedAt: (at: number | null) => void;
}) {
  const qc = useQueryClient();
  const metrics = useInfiniteQuery(metricsQuery(api, company.id));
  // WAVE12-FIX: the picker offers every metric, as Setup › Metrics and data entry do.
  useEveryPage(metrics);
  const [rows, setRows] = useState<Row[]>(initial);
  const [adding, setAdding] = useState("");
  const defs = (metrics.data?.pages.flatMap((p) => p.data) ?? []).filter((m) => !m.archived_at);
  const addable = defs.filter((m) => !rows.some((r) => r.metric_def_id === m.id));
  const invalid = rows.some((r) => !WEIGHT.test(r.kpi_weight) || !WEIGHT.test(r.goal_weight));
  const dirty = JSON.stringify(rows) !== JSON.stringify(initial);
  const save = useMutation({
    mutationFn: () =>
      api.client.seats.putMetrics({
        params: { co: company.id, id: seatId },
        ifMatch: version,
        body: {
          items: rows.map((r, position) => ({
            metric_def_id: r.metric_def_id,
            kpi_weight: r.kpi_weight,
            goal_weight: r.goal_weight,
            tracking_mode: r.tracking_mode,
            penalty_only: r.penalty_only,
            position,
          })),
        },
      }),
    onSuccess: (saved) => {
      setSavedAt(Date.now());
      qc.setQueryData(keys.seatMetricSet(company.id, seatId), saved);
      // Every scorecard that reads this seat (the seat, its DE, the company roll-up).
      void qc.invalidateQueries({
        queryKey: ["company", company.id],
        predicate: (q) => q.queryKey[4] !== "metric-set",
      });
    },
  });
  // Someone saved this seat's set since it was loaded: nothing was written.
  const stale = save.error instanceof ApiError && save.error.status === 409;
  const reload = () => {
    setSavedAt(null);
    void qc.invalidateQueries({ queryKey: keys.seatMetricSet(company.id, seatId) });
  };
  const set = (i: number, patch: Partial<Row>) => {
    setSavedAt(null);
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  };
  const move = (i: number, by: -1 | 1) => {
    setSavedAt(null);
    setRows((rs) => {
      const next = [...rs];
      const [row] = next.splice(i, 1);
      if (row) next.splice(i + by, 0, row);
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-4" data-slot="seat-metrics">
      {readOnly ? <ReadOnlyNotice what="the seat's metrics" /> : null}
      <Table>
        <TableCaption>
          The seat's metrics in scorecard order. A KPI weight scores the KPI; a goal weight counts
          the metric toward goals; context metrics are shown, never scored.
        </TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Metric</TableHead>
            <TableHead scope="col">Tracking</TableHead>
            <TableHead scope="col" numeric>
              KPI weight
            </TableHead>
            <TableHead scope="col" numeric>
              Goal weight
            </TableHead>
            <TableHead scope="col">Penalty only</TableHead>
            {readOnly ? null : (
              <TableHead scope="col">
                <span className="sr-only">Order and remove</span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length ? (
            rows.map((r, i) => (
              <TableRow key={r.metric_def_id} data-metric-row={r.metric_def_id}>
                <TableHead scope="row">{r.name}</TableHead>
                <TableCell>
                  <NativeSelect
                    size="sm"
                    aria-label={`Tracking of ${r.name}`}
                    value={r.tracking_mode}
                    disabled={readOnly}
                    onChange={(e) =>
                      set(i, { tracking_mode: e.target.value as Row["tracking_mode"] })
                    }
                  >
                    {TRACKING_OPTIONS.map((o) => (
                      <NativeSelectOption key={o.value} value={o.value}>
                        {o.label}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                </TableCell>
                <TableCell numeric>
                  <Input
                    className="ml-auto h-7 w-20 text-right"
                    inputMode="decimal"
                    aria-label={`KPI weight of ${r.name}`}
                    aria-invalid={WEIGHT.test(r.kpi_weight) ? undefined : true}
                    value={r.kpi_weight}
                    disabled={readOnly}
                    onChange={(e) => set(i, { kpi_weight: e.target.value })}
                  />
                </TableCell>
                <TableCell numeric>
                  <Input
                    className="ml-auto h-7 w-20 text-right"
                    inputMode="decimal"
                    aria-label={`Goal weight of ${r.name}`}
                    aria-invalid={WEIGHT.test(r.goal_weight) ? undefined : true}
                    value={r.goal_weight}
                    disabled={readOnly}
                    onChange={(e) => set(i, { goal_weight: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <Checkbox
                    aria-label={`Penalty only: ${r.name}`}
                    checked={r.penalty_only}
                    disabled={readOnly}
                    onCheckedChange={(v) => set(i, { penalty_only: v === true })}
                  />
                </TableCell>
                {readOnly ? null : (
                  <TableCell className="whitespace-nowrap text-right">
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label={`Move ${r.name} up`}
                      disabled={i === 0}
                      onClick={() => move(i, -1)}
                    >
                      <ArrowUpIcon />
                    </Button>
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label={`Move ${r.name} down`}
                      disabled={i === rows.length - 1}
                      onClick={() => move(i, 1)}
                    >
                      <ArrowDownIcon />
                    </Button>
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label={`Remove ${r.name}`}
                      onClick={() => {
                        setSavedAt(null);
                        setRows((rs) => rs.filter((_, j) => j !== i));
                      }}
                    >
                      <XIcon />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={readOnly ? 5 : 6} className="text-muted-foreground">
                No metrics on this seat yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {readOnly ? null : (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <Field className="min-w-56 flex-1">
              <FieldLabel htmlFor={`seat-add-metric-${seatId}`}>Add a metric</FieldLabel>
              <NativeSelect
                id={`seat-add-metric-${seatId}`}
                value={adding}
                onChange={(e) => setAdding(e.target.value)}
              >
                <NativeSelectOption value="">Choose a metric…</NativeSelectOption>
                {addable.map((m) => (
                  <NativeSelectOption key={m.id} value={m.id}>
                    {m.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Button
              variant="secondary"
              disabled={!adding}
              onClick={() => {
                const m = defs.find((x) => x.id === adding);
                if (!m) return;
                setSavedAt(null);
                setRows((rs) => [
                  ...rs,
                  {
                    metric_def_id: m.id,
                    name: m.name,
                    kpi_weight: "1",
                    goal_weight: "0",
                    tracking_mode: "scored",
                    penalty_only: false,
                  },
                ]);
                setAdding("");
              }}
            >
              Add
            </Button>
            {metrics.hasNextPage ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={metrics.isFetchingNextPage}
                onClick={() => void metrics.fetchNextPage()}
              >
                Load more metrics
              </Button>
            ) : null}
          </div>
          {stale ? (
            <Notice tone="warning" role="alert" title="The metrics changed since you opened them">
              Someone else saved this seat's metrics, so yours were not saved.{" "}
              <Button variant="link" size="sm" className="h-auto p-0" onClick={reload}>
                Load their version
              </Button>
            </Notice>
          ) : (
            <FormError what="Saving the metrics" error={save.error} />
          )}
          <div className="flex items-center gap-3">
            <Button disabled={!dirty || invalid || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? "Saving…" : "Save metrics"}
            </Button>
            <span role="status" className="text-sm text-muted-foreground">
              {invalid
                ? "Weights are numbers from 0 to 999.999."
                : savedAt && !dirty
                  ? "Metrics saved."
                  : ""}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
